import { Injectable } from '@angular/core'

import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs'
import { Apollo, gql } from 'apollo-angular'

import {
  GetTrygroupDashboardAccessGrantsGQL,
  GetTrygroupDashboardAccessProbeGQL,
  GrantTrygroupDashboardAccessGQL,
  RevokeTrygroupDashboardAccessGQL,
} from '../../../_generated/graphql'
import { throwIfCustomError } from '../../util/throwIfCustomError'
import { assertGraphqlData } from '../../util/assertGraphqlData'

export type TrygroupMonthlySalesPerformanceImportPerMonth = {
  yyyymm: string
  processedRows: number
  importedRecords: number
}

export type TrygroupMonthlySalesPerformanceImportWarning = {
  code: string
  message: string
  rowNumber?: number | null
  columnName?: string | null
  userEmail?: string | null
  yyyymm?: string | null
}

export type ImportTrygroupSalesPerformanceResult = {
  months: string[]
  totalProcessedRows: number
  totalImportedRecords: number
  perMonth: TrygroupMonthlySalesPerformanceImportPerMonth[]
  warnings: TrygroupMonthlySalesPerformanceImportWarning[]
}

type TrygroupMonthlySalesPerformanceUploadInfo = {
  uploadUrl: string
  objectPath: string
}

export type TrygroupAccessGrantUser = {
  id: string
  name: string
  email: string
  departmentName: string | null
  roles: {
    name: string
    code: string
  }[]
}

export type TrygroupDashboardAccessGrant = {
  id: string
  userId: string
  grantedAt: Date
  grantedById: string
}

export type TrygroupAccessGrantSetting = {
  user: TrygroupAccessGrantUser
  grant: TrygroupDashboardAccessGrant | null
  granted: boolean
  isSuperAdmin: boolean
}

@Injectable({
  providedIn: 'root',
})
export class TrygroupDashboardSettingsService {
  constructor(
    private apollo: Apollo,
    private getTrygroupDashboardAccessProbeGQL: GetTrygroupDashboardAccessProbeGQL,
    private getTrygroupDashboardAccessGrantsGQL: GetTrygroupDashboardAccessGrantsGQL,
    private grantTrygroupDashboardAccessGQL: GrantTrygroupDashboardAccessGQL,
    private revokeTrygroupDashboardAccessGQL: RevokeTrygroupDashboardAccessGQL,
  ) {}

  canAccessDashboard(): Observable<boolean> {
    return this.getTrygroupDashboardAccessProbeGQL
      .fetch({ fetchPolicy: 'no-cache' })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      )
  }

  getAccessGrantSettings(): Observable<TrygroupAccessGrantSetting[]> {
    return this.getTrygroupDashboardAccessGrantsGQL
      .fetch({ fetchPolicy: 'no-cache' })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          const grantsByUserId = new Map(
            response.data.trygroupDashboardAccessGrants.map((grant) => [
              grant.userId,
              mapAccessGrant(grant),
            ]),
          )

          return response.data.getAdminOrg.users.map((user) => {
            const grant = grantsByUserId.get(user.id) ?? null

            return {
              user: {
                id: user.id,
                name: user.name,
                email: user.email,
                departmentName: user.departmentName ?? null,
                roles: user.roles.map((role) => ({
                  name: role.name,
                  code: role.code,
                })),
              },
              grant,
              granted: grant != null,
              isSuperAdmin: user.roles.some(
                (role) => role.code === 'SUPER_ADMIN',
              ),
            }
          })
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** アクセスログCSV用の2D配列を取得する。 */
  exportAccessLog(startDate: string, endDate: string): Observable<string[][]> {
    return this.apollo
      .query<{ exportTrygroupAccessLog: string[][] }>({
        query: EXPORT_TRYGROUP_ACCESS_LOG_QUERY,
        variables: { startDate, endDate },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.exportTrygroupAccessLog
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** NGパターン分析CSV用の2D配列を取得する。 */
  exportNgAnalysis(): Observable<string[][]> {
    return this.apollo
      .query<{ exportTrygroupNgAnalysis: string[][] }>({
        query: EXPORT_TRYGROUP_NG_ANALYSIS_QUERY,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.exportTrygroupNgAnalysis
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 商談別NGパターン結果CSV用の2D配列を取得する。 */
  exportNgPatternResults(
    startDate: string,
    endDate: string,
  ): Observable<string[][]> {
    return this.apollo
      .query<{ exportTrygroupNgPatternResults: string[][] }>({
        query: EXPORT_TRYGROUP_NG_PATTERN_RESULTS_QUERY,
        variables: { startDate, endDate },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.exportTrygroupNgPatternResults
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  grantTrygroupDashboardAccess(
    userId: string,
  ): Observable<TrygroupDashboardAccessGrant> {
    return this.grantTrygroupDashboardAccessGQL
      .mutate({ variables: { userId } })
      .pipe(
        map((response) => {
          const grant = response.data?.grantTrygroupDashboardAccess
          if (!grant) {
            throw new Error('grantTrygroupDashboardAccess response is empty')
          }

          return mapAccessGrant(grant)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  revokeTrygroupDashboardAccess(userId: string): Observable<boolean> {
    return this.revokeTrygroupDashboardAccessGQL
      .mutate({ variables: { userId } })
      .pipe(
        map((response) => {
          const revoked = response.data?.revokeTrygroupDashboardAccess
          if (revoked == null) {
            throw new Error('revokeTrygroupDashboardAccess response is empty')
          }

          return revoked
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  importMonthlySalesPerformance(
    file: File,
  ): Observable<ImportTrygroupSalesPerformanceResult> {
    return this.apollo
      .mutate<{
        createTrygroupMonthlySalesPerformanceUploadUrl: TrygroupMonthlySalesPerformanceUploadInfo
      }>({
        mutation: CREATE_TRYGROUP_MONTHLY_SALES_PERFORMANCE_UPLOAD_URL_MUTATION,
        variables: { filename: file.name },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          const payload =
            response.data?.createTrygroupMonthlySalesPerformanceUploadUrl
          if (!payload) {
            throw new Error(
              'createTrygroupMonthlySalesPerformanceUploadUrl response is empty',
            )
          }

          return payload
        }),
        switchMap((uploadInfo) =>
          from(
            uploadMonthlySalesPerformanceFile(uploadInfo.uploadUrl, file),
          ).pipe(map(() => uploadInfo.objectPath)),
        ),
        switchMap((objectPath) =>
          this.apollo.mutate<{
            importTrygroupSalesPerformance: ImportTrygroupSalesPerformanceResult
          }>({
            mutation: IMPORT_TRYGROUP_SALES_PERFORMANCE_MUTATION,
            variables: { objectPath },
            fetchPolicy: 'no-cache',
          }),
        ),
        map((response) => {
          const payload = response.data?.importTrygroupSalesPerformance
          if (!payload) {
            throw new Error('importTrygroupSalesPerformance response is empty')
          }

          return payload
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }
}

const XLSX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
// backend の TRYGROUP_MONTHLY_SALES_IMPORT_MAX_UPLOAD_BYTES と一致させる。
// V4 signed URL の x-goog-content-length-range extension header は署名対象に
// 含まれているため、PUT 時に同じ値を送らないと GCS が 403 で拒否する。
const TRYGROUP_MONTHLY_SALES_IMPORT_MAX_UPLOAD_BYTES = 10_000_000

const CREATE_TRYGROUP_MONTHLY_SALES_PERFORMANCE_UPLOAD_URL_MUTATION = gql`
  mutation CreateTrygroupMonthlySalesPerformanceUploadUrl($filename: String!) {
    createTrygroupMonthlySalesPerformanceUploadUrl(filename: $filename) {
      uploadUrl
      objectPath
    }
  }
`

const IMPORT_TRYGROUP_SALES_PERFORMANCE_MUTATION = gql`
  mutation ImportTrygroupSalesPerformance($objectPath: String!) {
    importTrygroupSalesPerformance(objectPath: $objectPath) {
      months
      totalProcessedRows
      totalImportedRecords
      perMonth {
        yyyymm
        processedRows
        importedRecords
      }
      warnings {
        code
        message
        rowNumber
        columnName
        userEmail
        yyyymm
      }
    }
  }
`

const EXPORT_TRYGROUP_ACCESS_LOG_QUERY = gql`
  query ExportTrygroupAccessLog($startDate: String!, $endDate: String!) {
    exportTrygroupAccessLog(startDate: $startDate, endDate: $endDate)
  }
`

const EXPORT_TRYGROUP_NG_ANALYSIS_QUERY = gql`
  query ExportTrygroupNgAnalysis {
    exportTrygroupNgAnalysis
  }
`

const EXPORT_TRYGROUP_NG_PATTERN_RESULTS_QUERY = gql`
  query ExportTrygroupNgPatternResults($startDate: String!, $endDate: String!) {
    exportTrygroupNgPatternResults(startDate: $startDate, endDate: $endDate)
  }
`

async function uploadMonthlySalesPerformanceFile(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const contentType = file.type === '' ? XLSX_CONTENT_TYPE : file.type
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
      'x-goog-content-length-range': `0,${TRYGROUP_MONTHLY_SALES_IMPORT_MAX_UPLOAD_BYTES}`,
    },
  })

  if (!response.ok) {
    throw new Error('xlsx のアップロードに失敗しました。')
  }
}

function mapAccessGrant(grant: {
  id: string
  userId: string
  grantedAt: Date
  grantedById: string
}): TrygroupDashboardAccessGrant {
  return {
    id: grant.id,
    userId: grant.userId,
    grantedAt: new Date(grant.grantedAt),
    grantedById: grant.grantedById,
  }
}
